import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  TrendingDown,
  Users,
  Download,
  Search,
} from 'lucide-react'
import DealerTrendChart from './DealerTrendChart'
import styles from './DealerPerformance.module.scss'
import { config } from '../../config'

interface TrendPoint {
  date: string
  cars: number
  published: number
  failed: number
  db_missing: number
}

interface AlertDealer {
  dealer_id: string
  dealer_name: string
  baseline_days_present: number
  baseline_avg_cars: number
  today_cars: number
  codes: string
  window_total: number
  trend: TrendPoint[]
  status: string
  ratio_to_baseline?: number
}

interface AlertsData {
  target_date: string
  baseline_dates: string[]
  missing: AlertDealer[]
  drops: AlertDealer[]
  total_missing: number
  total_drops: number
  error?: string
}

interface DealerSummary {
  dealer_id: string
  dealer_name: string
  codes: string
  total_records: number
  present_days: number
  total_days: number
  today_records: number
  baseline_avg: number
  published_today: number
  failed_today: number
  db_missing_today: number
  trend: TrendPoint[]
}

interface SummaryData {
  target_date: string
  dates: string[]
  total_dealers: number
  dealers: DealerSummary[]
  error?: string
}

type SortField = 'dealer_name' | 'today_records' | 'baseline_avg' | 'present_days' | 'published_today' | 'failed_today' | 'db_missing_today' | 'total_records'
type SortDir = 'asc' | 'desc'

interface DealerPerformanceProps {
  datePattern: string
}

const PAGE_SIZE = 25

const DealerPerformance: React.FC<DealerPerformanceProps> = ({ datePattern }) => {
  const [baselineDays, setBaselineDays] = useState(7)
  const [alertBaselineDays, setAlertBaselineDays] = useState(3)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const [alertsData, setAlertsData] = useState<AlertsData | null>(null)
  const [alertsLoading, setAlertsLoading] = useState(false)
  const [missingOpen, setMissingOpen] = useState(false)
  const [dropsOpen, setDropsOpen] = useState(false)

  const [summaryData, setSummaryData] = useState<SummaryData | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(false)

  const [sortField, setSortField] = useState<SortField>('today_records')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [page, setPage] = useState(1)

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300)
    return () => clearTimeout(t)
  }, [searchQuery])

  const fetchAlerts = useCallback(async () => {
    setAlertsLoading(true)
    try {
      const params = new URLSearchParams({
        date: datePattern,
        baseline_days: String(alertBaselineDays),
      })
      const res = await fetch(`${config.MONITOR_API_URL}/insights/dealer-alerts?${params}`)
      const data: AlertsData = await res.json()
      setAlertsData(data)
      if (data.total_missing > 0) setMissingOpen(true)
      if (data.total_drops > 0) setDropsOpen(true)
    } catch (e) {
      console.error('Failed to fetch dealer alerts', e)
    } finally {
      setAlertsLoading(false)
    }
  }, [datePattern, alertBaselineDays])

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true)
    try {
      const params = new URLSearchParams({
        date: datePattern,
        baseline_days: String(baselineDays),
      })
      const res = await fetch(`${config.MONITOR_API_URL}/insights/dealer-summary?${params}`)
      const data: SummaryData = await res.json()
      setSummaryData(data)
    } catch (e) {
      console.error('Failed to fetch dealer summary', e)
    } finally {
      setSummaryLoading(false)
    }
  }, [datePattern, baselineDays])

  useEffect(() => {
    fetchAlerts()
    fetchSummary()
  }, [fetchAlerts, fetchSummary])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, sortField, sortDir])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir(field === 'dealer_name' ? 'asc' : 'desc')
    }
  }

  const filteredDealers = useMemo(() => {
    if (!summaryData?.dealers) return []
    let list = summaryData.dealers
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase()
      list = list.filter(
        (d) =>
          d.dealer_name.toLowerCase().includes(q) ||
          d.dealer_id.toLowerCase().includes(q) ||
          d.codes.toLowerCase().includes(q),
      )
    }
    return [...list].sort((a, b) => {
      const aVal = a[sortField]
      const bVal = b[sortField]
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      const diff = (aVal as number) - (bVal as number)
      return sortDir === 'asc' ? diff : -diff
    })
  }, [summaryData, debouncedSearch, sortField, sortDir])

  const totalPages = Math.max(1, Math.ceil(filteredDealers.length / PAGE_SIZE))
  const pagedDealers = filteredDealers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const handleRefresh = () => {
    fetchAlerts()
    fetchSummary()
  }

  const exportCSV = () => {
    if (!filteredDealers.length) return
    const headers = ['Dealer Name', 'Dealer ID', 'Codes', 'Today', 'Baseline Avg', 'Presence', 'Published', 'Failed', 'DB Missing', 'Window Total']
    let csv = headers.join(',') + '\n'
    filteredDealers.forEach((d) => {
      csv += [
        `"${d.dealer_name.replace(/"/g, '""')}"`,
        d.dealer_id,
        `"${d.codes}"`,
        d.today_records,
        d.baseline_avg,
        `${d.present_days}/${d.total_days}`,
        d.published_today,
        d.failed_today,
        d.db_missing_today,
        d.total_records,
      ].join(',') + '\n'
    })
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `dealer-performance-${datePattern}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return <span className={styles.sortIcon}><ChevronDown size={12} /></span>
    return (
      <span className={`${styles.sortIcon} ${styles.sortActive}`}>
        {sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </span>
    )
  }

  const isLoading = alertsLoading || summaryLoading
  const alertTotal = (alertsData?.total_missing ?? 0) + (alertsData?.total_drops ?? 0)

  return (
    <div className={styles.container}>
      {/* Controls Bar */}
      <div className={styles.controls}>
        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>Window</span>
          <select
            className={styles.select}
            value={baselineDays}
            onChange={(e) => setBaselineDays(parseInt(e.target.value))}>
            <option value={3}>3 days</option>
            <option value={5}>5 days</option>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
          </select>
        </div>

        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>Alert Baseline</span>
          <select
            className={styles.select}
            value={alertBaselineDays}
            onChange={(e) => setAlertBaselineDays(parseInt(e.target.value))}>
            <option value={2}>2 days</option>
            <option value={3}>3 days</option>
            <option value={5}>5 days</option>
            <option value={7}>7 days</option>
          </select>
        </div>

        <div className={styles.controlGroup}>
          <Search size={14} style={{ color: 'var(--color-text-muted)' }} />
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Filter by dealer name, ID, or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className={styles.controlSpacer} />

        <button className={styles.refreshBtn} onClick={handleRefresh} disabled={isLoading}>
          <RefreshCw size={14} className={isLoading ? styles.spinning : ''} />
          Refresh
        </button>

        <button className={styles.exportBtn} onClick={exportCSV} disabled={!filteredDealers.length}>
          <Download size={14} />
          Export
        </button>
      </div>

      {/* Alerts Section */}
      {alertsLoading ? (
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Analyzing dealer coverage...</span>
        </div>
      ) : alertsData && !alertsData.error ? (
        <div className={styles.alertsGrid}>
          {/* Missing Dealers Card */}
          <div className={`${styles.alertCard} ${styles.alertCardMissing}`}>
            <div className={styles.alertHeader} onClick={() => setMissingOpen(!missingOpen)}>
              <div className={styles.alertHeaderLeft}>
                <div className={`${styles.alertIcon} ${styles.alertIconMissing}`}>
                  <AlertTriangle size={16} />
                </div>
                <div>
                  <div className={styles.alertTitle}>Missing Dealers</div>
                  <div className={styles.alertSubtitle}>
                    Not seen on {datePattern} vs {alertsData.baseline_dates?.length ?? 0}-day baseline
                  </div>
                </div>
              </div>
              <span className={`${styles.alertCount} ${styles.alertCountMissing}`}>
                {alertsData.total_missing}
              </span>
              <div className={`${styles.alertToggle} ${missingOpen ? styles.alertToggleOpen : ''}`}>
                <ChevronDown size={16} />
              </div>
            </div>
            {missingOpen && (
              <div className={styles.alertBody}>
                {alertsData.missing.length === 0 ? (
                  <div className={styles.alertEmpty}>No missing dealers detected</div>
                ) : (
                  <table className={styles.alertTable}>
                    <thead>
                      <tr>
                        <th>Dealer</th>
                        <th>Baseline Avg</th>
                        <th>Days Present</th>
                        <th>Codes</th>
                        <th>Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alertsData.missing.map((d) => (
                        <tr key={d.dealer_id}>
                          <td>
                            <div className={styles.dealerName}>{d.dealer_name}</div>
                            <div className={styles.dealerId}>{d.dealer_id}</div>
                          </td>
                          <td className={styles.monoValue}>{d.baseline_avg_cars}</td>
                          <td className={styles.monoValue}>{d.baseline_days_present}</td>
                          <td>{d.codes && <span className={styles.codesBadge}>{d.codes}</span>}</td>
                          <td className={styles.trendCell}>
                            <DealerTrendChart data={d.trend} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          {/* Major Drops Card */}
          <div className={`${styles.alertCard} ${styles.alertCardDrop}`}>
            <div className={styles.alertHeader} onClick={() => setDropsOpen(!dropsOpen)}>
              <div className={styles.alertHeaderLeft}>
                <div className={`${styles.alertIcon} ${styles.alertIconDrop}`}>
                  <TrendingDown size={16} />
                </div>
                <div>
                  <div className={styles.alertTitle}>Major Drops</div>
                  <div className={styles.alertSubtitle}>
                    Car count &lt;30% of baseline average
                  </div>
                </div>
              </div>
              <span className={`${styles.alertCount} ${styles.alertCountDrop}`}>
                {alertsData.total_drops}
              </span>
              <div className={`${styles.alertToggle} ${dropsOpen ? styles.alertToggleOpen : ''}`}>
                <ChevronDown size={16} />
              </div>
            </div>
            {dropsOpen && (
              <div className={styles.alertBody}>
                {alertsData.drops.length === 0 ? (
                  <div className={styles.alertEmpty}>No major drops detected</div>
                ) : (
                  <table className={styles.alertTable}>
                    <thead>
                      <tr>
                        <th>Dealer</th>
                        <th>Today</th>
                        <th>Baseline Avg</th>
                        <th>Ratio</th>
                        <th>Codes</th>
                        <th>Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alertsData.drops.map((d) => (
                        <tr key={d.dealer_id}>
                          <td>
                            <div className={styles.dealerName}>{d.dealer_name}</div>
                            <div className={styles.dealerId}>{d.dealer_id}</div>
                          </td>
                          <td className={styles.monoValue}>{d.today_cars}</td>
                          <td className={styles.monoValue}>{d.baseline_avg_cars}</td>
                          <td>
                            <span
                              className={`${styles.ratioCell} ${
                                (d.ratio_to_baseline ?? 0) < 0.1 ? styles.ratioLow : styles.ratioMedium
                              }`}>
                              {((d.ratio_to_baseline ?? 0) * 100).toFixed(1)}%
                            </span>
                          </td>
                          <td>{d.codes && <span className={styles.codesBadge}>{d.codes}</span>}</td>
                          <td className={styles.trendCell}>
                            <DealerTrendChart data={d.trend} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      ) : alertsData?.error ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><AlertTriangle size={36} /></div>
          <h3>No Baseline Data</h3>
          <p>{alertsData.error}</p>
        </div>
      ) : null}

      {/* Dealer Summary Table */}
      {summaryLoading ? (
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Loading dealer summaries...</span>
        </div>
      ) : summaryData && !summaryData.error ? (
        <div className={styles.summarySection}>
          <div className={styles.summaryHeader}>
            <div className={styles.summaryTitle}>
              <Users size={18} />
              Dealer Summary
              <span className={styles.summaryCount}>
                {filteredDealers.length} of {summaryData.total_dealers} dealers
              </span>
            </div>
          </div>
          <div className={styles.summaryTableWrap}>
            <table className={styles.summaryTable}>
              <thead>
                <tr>
                  <th onClick={() => handleSort('dealer_name')}>
                    Dealer {renderSortIcon('dealer_name')}
                  </th>
                  <th>Codes</th>
                  <th onClick={() => handleSort('today_records')}>
                    Today {renderSortIcon('today_records')}
                  </th>
                  <th onClick={() => handleSort('baseline_avg')}>
                    Baseline Avg {renderSortIcon('baseline_avg')}
                  </th>
                  <th onClick={() => handleSort('present_days')}>
                    Presence {renderSortIcon('present_days')}
                  </th>
                  <th onClick={() => handleSort('published_today')}>
                    Published {renderSortIcon('published_today')}
                  </th>
                  <th onClick={() => handleSort('failed_today')}>
                    Failed {renderSortIcon('failed_today')}
                  </th>
                  <th onClick={() => handleSort('db_missing_today')}>
                    DB Missing {renderSortIcon('db_missing_today')}
                  </th>
                  <th>Trend</th>
                </tr>
              </thead>
              <tbody>
                {pagedDealers.map((d) => (
                  <tr key={d.dealer_id}>
                    <td>
                      <div className={styles.dealerName} title={d.dealer_name}>{d.dealer_name}</div>
                      <div className={styles.dealerId}>{d.dealer_id}</div>
                    </td>
                    <td>{d.codes && <span className={styles.codesBadge}>{d.codes}</span>}</td>
                    <td className={styles.monoValue}>{d.today_records.toLocaleString()}</td>
                    <td className={styles.monoValue}>{d.baseline_avg}</td>
                    <td>
                      <div className={styles.presenceBar}>
                        <div className={styles.presenceTrack}>
                          {Array.from({ length: d.total_days }, (_, i) => (
                            <div
                              key={i}
                              className={`${styles.presenceDot} ${
                                d.trend[i]?.cars > 0 ? styles.presenceDotActive : ''
                              }`}
                            />
                          ))}
                        </div>
                        <span className={styles.presenceLabel}>
                          {d.present_days}/{d.total_days}
                        </span>
                      </div>
                    </td>
                    <td>
                      {d.published_today > 0 && (
                        <span className={`${styles.statusBadge} ${styles.statusPublished}`}>
                          {d.published_today.toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td>
                      {d.failed_today > 0 && (
                        <span className={`${styles.statusBadge} ${styles.statusFailed}`}>
                          {d.failed_today.toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td>
                      {d.db_missing_today > 0 && (
                        <span className={`${styles.statusBadge} ${styles.statusMissing}`}>
                          {d.db_missing_today.toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td className={styles.trendCell}>
                      <DealerTrendChart data={d.trend} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <span className={styles.pageInfo}>
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredDealers.length)} of{' '}
                {filteredDealers.length}
              </span>
              <div className={styles.pageControls}>
                <button className={styles.pageBtn} onClick={() => setPage(1)} disabled={page === 1}>
                  <ChevronLeft size={12} /><ChevronLeft size={12} style={{ marginLeft: -8 }} />
                </button>
                <button className={styles.pageBtn} onClick={() => setPage(page - 1)} disabled={page === 1}>
                  <ChevronLeft size={14} />
                </button>
                <span className={styles.pageInfo} style={{ margin: '0 8px' }}>
                  {page} / {totalPages}
                </span>
                <button className={styles.pageBtn} onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                  <ChevronRight size={14} />
                </button>
                <button className={styles.pageBtn} onClick={() => setPage(totalPages)} disabled={page >= totalPages}>
                  <ChevronRight size={12} /><ChevronRight size={12} style={{ marginLeft: -8 }} />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : summaryData?.error ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><Users size={36} /></div>
          <h3>No Data Available</h3>
          <p>{summaryData.error}</p>
        </div>
      ) : null}

      {/* Global empty state if nothing loaded */}
      {!alertsLoading && !summaryLoading && !alertsData && !summaryData && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><Users size={48} /></div>
          <h3>No Dealer Data</h3>
          <p>
            No daily-feed-metrics indices found for {datePattern}. Generate insights from the
            Overview tab first.
          </p>
        </div>
      )}
    </div>
  )
}

export default DealerPerformance
